import { useState, useEffect } from 'react';
import { supabase } from '../../lib/supabase';
import ReceiptList from './ReceiptList';
import AnalyticsCards from './AnalyticsCards';
import ReceiptDetail from './ReceiptDetail';
import Modal from './Modal';
import { Download, Lock, X, Crown } from 'lucide-react';
import type { Database } from '../../lib/database.types';

type Receipt = Database['public']['Tables']['receipts']['Row'] & {
  categories?: Database['public']['Tables']['categories']['Row'];
};

interface DashboardProps {
  devModeUserId?: string | null;
}

type AccessLevel = 'free' | 'premium' | 'enterprise';

export default function Dashboard({ devModeUserId }: DashboardProps = {}) {
  const [receipts, setReceipts] = useState<Receipt[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedReceipt, setSelectedReceipt] = useState<Receipt | null>(null);
  const [filterStatus, setFilterStatus] = useState<string>('all');
  const [filterCategory, setFilterCategory] = useState<string>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [categories, setCategories] = useState<Array<{ id: string; name: string }>>([]);
  const [accessLevel, setAccessLevel] = useState<AccessLevel>('free');
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [errorModal, setErrorModal] = useState<{ show: boolean; title: string; message: string }>({
    show: false,
    title: '',
    message: '',
  });

  useEffect(() => {
    fetchUserAccess();
    fetchCategories();
  }, [devModeUserId]);

  useEffect(() => {
    if (devModeUserId) {
      fetchReceipts();
    }
  }, [filterStatus, filterCategory, startDate, endDate, devModeUserId, accessLevel]);

  async function fetchUserAccess() {
    try {
      console.log('fetchUserAccess called with devModeUserId:', devModeUserId);

      if (!devModeUserId) {
        console.log('No devModeUserId provided, skipping access level fetch');
        return;
      }

      const { data: user, error: userError } = await supabase
        .from('users')
        .select('telegram_id')
        .eq('id', devModeUserId)
        .maybeSingle();

      console.log('User lookup result:', { user, userError });

      if (userError) {
        console.error('Error fetching user telegram_id:', userError);
        return;
      }

      if (!user?.telegram_id) {
        console.log('No telegram_id found for user');
        return;
      }

      console.log('Looking up access level for telegram_id:', user.telegram_id);

      const { data, error } = await supabase
        .from('user_access')
        .select('access_level')
        .eq('telegram_id', user.telegram_id)
        .maybeSingle();

      console.log('Access level lookup result:', { data, error });

      if (error) {
        console.error('Error fetching user access:', error);
        return;
      }

      if (data) {
        console.log('Setting access level to:', data.access_level);
        setAccessLevel(data.access_level as AccessLevel);
      } else {
        console.log('No access level data found, defaulting to free');
      }
    } catch (error) {
      console.error('Error in fetchUserAccess:', error);
    }
  }

  async function fetchCategories() {
    try {
      const { data, error } = await supabase
        .from('categories')
        .select('id, name')
        .order('name');

      if (error) throw error;
      setCategories(data || []);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  }

  async function fetchReceipts() {
    try {
      setLoading(true);
      console.log('Fetching receipts with devModeUserId:', devModeUserId, 'accessLevel:', accessLevel);

      let query = supabase
        .from('receipts')
        .select('*, categories(*)')
        .order('created_at', { ascending: false });

      if (devModeUserId) {
        query = query.eq('user_id', devModeUserId);
      }

      if (accessLevel === 'free' && !startDate && !endDate) {
        const now = new Date();
        const firstDayOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
        console.log('Free tier restriction: filtering receipts from', firstDayOfMonth.toISOString());
        query = query.gte('receipt_date', firstDayOfMonth.toISOString());
      }

      if (filterStatus !== 'all') {
        query = query.eq('status', filterStatus);
      }

      if (filterCategory !== 'all') {
        query = query.eq('category_id', filterCategory);
      }

      if (startDate) {
        query = query.gte('receipt_date', startDate);
      }

      if (endDate) {
        query = query.lte('receipt_date', endDate);
      }

      const { data, error } = await query;

      if (error) {
        console.error('Error fetching receipts:', error);
        throw error;
      }

      console.log('Fetched receipts:', data?.length || 0, 'receipts');
      setReceipts(data || []);
    } catch (error) {
      console.error('Error fetching receipts:', error);
    } finally {
      setLoading(false);
    }
  }

  const filteredReceipts = receipts.filter((receipt) => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      receipt.merchant_name?.toLowerCase().includes(query) ||
      receipt.receipt_number?.toLowerCase().includes(query) ||
      receipt.notes?.toLowerCase().includes(query)
    );
  });

  const handleReceiptUpdate = () => {
    fetchReceipts();
    setSelectedReceipt(null);
  };

  const setDateRange = (days: number) => {
    const end = new Date();
    const start = new Date();
    start.setDate(start.getDate() - days);

    setStartDate(start.toISOString().split('T')[0]);
    setEndDate(end.toISOString().split('T')[0]);
  };

  const setCurrentMonth = () => {
    const now = new Date();
    const start = new Date(now.getFullYear(), now.getMonth(), 1);
    const end = new Date(now.getFullYear(), now.getMonth() + 1, 0);

    setStartDate(start.toISOString().split('T')[0]);
    setEndDate(end.toISOString().split('T')[0]);
  };

  const exportToCSV = () => {
    if (accessLevel === 'free') {
      setShowUpgradeModal(true);
      return;
    }

    const headers = ['Date', 'Merchant', 'Category', 'Amount', 'Currency', 'Tax Amount', 'Payment Method', 'Receipt Number', 'Status', 'Notes'];

    const rows = filteredReceipts.map(receipt => [
      receipt.receipt_date ? new Date(receipt.receipt_date).toLocaleDateString() : '',
      receipt.merchant_name || '',
      receipt.categories?.name || 'Uncategorized',
      (receipt.total_amount || 0).toFixed(2),
      receipt.currency || 'SGD',
      (receipt.tax_amount || 0).toFixed(2),
      receipt.payment_method || '',
      receipt.receipt_number || '',
      receipt.status || '',
      receipt.notes || ''
    ]);

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `receipts-${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  console.log('Rendering Dashboard with accessLevel:', accessLevel, 'devModeUserId:', devModeUserId);

  return (
    <div className="space-y-6">
      {accessLevel === 'free' && (
        <div className="bg-gradient-to-r from-amber-50 to-orange-50 dark:from-amber-900/20 dark:to-orange-900/20 border-2 border-amber-200 dark:border-amber-700 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <Lock className="w-5 h-5 text-amber-600 dark:text-amber-400 mt-0.5 flex-shrink-0" />
            <div className="flex-1">
              <h3 className="font-semibold text-amber-900 dark:text-amber-100 mb-1">Free Plan Restrictions</h3>
              <p className="text-sm text-amber-800 dark:text-amber-200">
                You can view receipts from the current month only. Export functionality and historical data access require a premium or enterprise plan.
              </p>
              <button
                onClick={() => setShowUpgradeModal(true)}
                className="mt-3 inline-flex items-center gap-2 px-4 py-2 bg-amber-600 hover:bg-amber-700 text-white rounded-lg transition-colors text-sm font-medium"
              >
                <Crown className="w-4 h-4" />
                Upgrade Now
              </button>
            </div>
          </div>
        </div>
      )}

      <AnalyticsCards receipts={receipts} />

      <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
        <div className="p-3 sm:p-6 border-b border-slate-200">
          <div className="space-y-4">
            <div className="flex flex-col space-y-3">
              <div>
                <h2 className="text-xl sm:text-2xl font-bold text-slate-800">Receipts</h2>
                <p className="text-xs sm:text-sm text-slate-600 mt-1">
                  Showing {filteredReceipts.length} of {receipts.length} receipts
                </p>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 xl:flex xl:flex-wrap gap-2 w-full">
                <input
                  type="text"
                  placeholder="Search..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full sm:col-span-2 lg:col-span-4 xl:flex-1 xl:min-w-[200px] px-3 py-2 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                />
                <select
                  value={filterStatus}
                  onChange={(e) => setFilterStatus(e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                >
                  <option value="all">All Status</option>
                  <option value="parsed">Parsed</option>
                  <option value="pending">Pending</option>
                  <option value="failed">Failed</option>
                </select>
                <select
                  value={filterCategory}
                  onChange={(e) => setFilterCategory(e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                >
                  <option value="all">All Categories</option>
                  {categories.map(cat => (
                    <option key={cat.id} value={cat.id}>{cat.name}</option>
                  ))}
                </select>
                <button
                  onClick={exportToCSV}
                  disabled={filteredReceipts.length === 0}
                  className="w-full sm:col-span-2 lg:col-span-1 flex items-center justify-center gap-2 px-3 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-medium text-sm whitespace-nowrap"
                >
                  <Download className="w-4 h-4" />
                  <span>Export CSV</span>
                </button>
              </div>
            </div>

            <div className="space-y-3">
              <div className="flex flex-wrap gap-2">
                <span className="text-sm font-medium text-slate-700 self-center">Quick Filters:</span>
                <button
                  onClick={() => accessLevel === 'free' ? setShowUpgradeModal(true) : setDateRange(7)}
                  className={`px-3 py-1 text-sm rounded-lg transition-colors ${
                    accessLevel === 'free'
                      ? 'bg-slate-100 text-slate-400 cursor-not-allowed'
                      : 'bg-slate-100 hover:bg-slate-200 text-slate-700'
                  }`}
                  disabled={accessLevel === 'free'}
                >
                  Last 7 days {accessLevel === 'free' && <Lock className="inline w-3 h-3 ml-1" />}
                </button>
                <button
                  onClick={() => accessLevel === 'free' ? setShowUpgradeModal(true) : setDateRange(30)}
                  className={`px-3 py-1 text-sm rounded-lg transition-colors ${
                    accessLevel === 'free'
                      ? 'bg-slate-100 text-slate-400 cursor-not-allowed'
                      : 'bg-slate-100 hover:bg-slate-200 text-slate-700'
                  }`}
                  disabled={accessLevel === 'free'}
                >
                  Last 30 days {accessLevel === 'free' && <Lock className="inline w-3 h-3 ml-1" />}
                </button>
                <button
                  onClick={setCurrentMonth}
                  className="px-3 py-1 text-sm bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-lg transition-colors"
                >
                  This Month
                </button>
              </div>

              <div className="flex flex-wrap gap-3 items-center">
                <span className="text-sm font-medium text-slate-700">Custom Range:</span>
                {accessLevel === 'free' ? (
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => setShowUpgradeModal(true)}
                      className="flex items-center gap-2 px-4 py-2 bg-slate-100 text-slate-400 rounded-lg cursor-not-allowed text-sm"
                      disabled
                    >
                      <Lock className="w-4 h-4" />
                      <span>Premium Feature</span>
                    </button>
                  </div>
                ) : (
                  <div className="flex items-center gap-2">
                    <input
                      type="date"
                      value={startDate}
                      onChange={(e) => setStartDate(e.target.value)}
                      className="px-4 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent text-sm"
                      placeholder="Start date"
                    />
                    <span className="text-slate-600">to</span>
                    <input
                      type="date"
                      value={endDate}
                      onChange={(e) => setEndDate(e.target.value)}
                      className="px-4 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent text-sm"
                      placeholder="End date"
                    />
                  </div>
                )}
                {(startDate || endDate) && (
                  <button
                    onClick={() => {
                      setStartDate('');
                      setEndDate('');
                    }}
                    className="px-3 py-2 text-sm text-slate-600 hover:text-slate-800 hover:bg-slate-100 rounded-lg transition-colors"
                  >
                    Clear Dates
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>

        {loading ? (
          <div className="p-12 text-center">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600"></div>
            <p className="mt-4 text-slate-600">Loading receipts...</p>
          </div>
        ) : (
          <ReceiptList
            receipts={filteredReceipts}
            onSelectReceipt={setSelectedReceipt}
            onRetry={fetchReceipts}
          />
        )}
      </div>

      {selectedReceipt && (
        <ReceiptDetail
          receipt={selectedReceipt}
          onClose={() => setSelectedReceipt(null)}
          onUpdate={handleReceiptUpdate}
        />
      )}

      {showUpgradeModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-gradient-to-br from-amber-400 to-orange-500 rounded-xl flex items-center justify-center">
                    <Crown className="w-6 h-6 text-white" />
                  </div>
                  <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Upgrade Your Plan</h2>
                </div>
                <button
                  onClick={() => setShowUpgradeModal(false)}
                  className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                >
                  <X className="w-5 h-5 text-gray-500 dark:text-gray-400" />
                </button>
              </div>

              <div className="space-y-4 mb-6">
                <div className="border-2 border-amber-200 dark:border-amber-700 rounded-lg p-4 bg-amber-50 dark:bg-amber-900/20">
                  <div className="flex items-start gap-3">
                    <Lock className="w-5 h-5 text-amber-600 dark:text-amber-400 mt-0.5 flex-shrink-0" />
                    <div>
                      <h3 className="font-semibold text-amber-900 dark:text-amber-100 mb-2">Feature Locked</h3>
                      <p className="text-sm text-amber-800 dark:text-amber-200">
                        Export functionality and access to historical data beyond the current month are only available on premium and enterprise plans.
                      </p>
                    </div>
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-4">
                  <div className="border-2 border-blue-200 dark:border-blue-700 rounded-lg p-5 bg-blue-50 dark:bg-blue-900/20">
                    <h3 className="text-xl font-bold text-blue-900 dark:text-blue-100 mb-2">Premium</h3>
                    <p className="text-3xl font-bold text-blue-600 dark:text-blue-400 mb-4">$9.90<span className="text-base font-normal text-blue-700 dark:text-blue-300">/month</span></p>
                    <ul className="space-y-2 mb-4">
                      <li className="flex items-start gap-2 text-sm text-blue-900 dark:text-blue-100">
                        <span className="text-blue-600 dark:text-blue-400 mt-0.5">✓</span>
                        <span>100 receipts per month</span>
                      </li>
                      <li className="flex items-start gap-2 text-sm text-blue-900 dark:text-blue-100">
                        <span className="text-blue-600 dark:text-blue-400 mt-0.5">✓</span>
                        <span>Full history access</span>
                      </li>
                      <li className="flex items-start gap-2 text-sm text-blue-900 dark:text-blue-100">
                        <span className="text-blue-600 dark:text-blue-400 mt-0.5">✓</span>
                        <span>CSV export</span>
                      </li>
                      <li className="flex items-start gap-2 text-sm text-blue-900 dark:text-blue-100">
                        <span className="text-blue-600 dark:text-blue-400 mt-0.5">✓</span>
                        <span>Advanced analytics</span>
                      </li>
                    </ul>
                    <button
                      onClick={async () => {
                        try {
                          // Get current user
                          console.log('Payment button clicked, devModeUserId:', devModeUserId);

                          const { data: user, error: userError } = await supabase
                            .from('users')
                            .select('telegram_id, first_name, last_name')
                            .eq('id', devModeUserId)
                            .maybeSingle();

                          console.log('User lookup result:', { user, userError });

                          if (userError) {
                            console.error('Error fetching user:', userError);
                            setErrorModal({
                              show: true,
                              title: 'Database Error',
                              message: `Failed to fetch user data: ${userError.message}`,
                            });
                            return;
                          }

                          if (!user?.telegram_id) {
                            setErrorModal({
                              show: true,
                              title: 'User Not Found',
                              message: 'We could not find your user account. Please make sure you are logged in through the Telegram bot first.',
                            });
                            return;
                          }

                          // Create checkout session via edge function
                          const response = await fetch(
                            `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/stripe-webhook`,
                            {
                              method: 'POST',
                              headers: {
                                'Content-Type': 'application/json',
                                'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
                              },
                              body: JSON.stringify({
                                action: 'create_checkout',
                                plan: 'premium',
                                user_id: user.telegram_id,
                                user_name: `${user.first_name || ''} ${user.last_name || ''}`.trim(),
                                success_url: `${window.location.origin}?session_id={CHECKOUT_SESSION_ID}`,
                                cancel_url: window.location.href,
                              }),
                            }
                          );

                          const data = await response.json();

                          if (data.url) {
                            // Open Stripe checkout in new tab to avoid iframe restrictions
                            window.open(data.url, '_blank');
                          } else if (data.error) {
                            let errorMessage = data.error;

                            // Provide more helpful message if Stripe is not configured
                            if (data.error.includes('not configured')) {
                              errorMessage = 'Payment system is currently being set up. Please contact the administrator or try again later.';
                            }

                            setErrorModal({
                              show: true,
                              title: 'Payment Unavailable',
                              message: errorMessage,
                            });
                          } else {
                            setErrorModal({
                              show: true,
                              title: 'Payment Error',
                              message: 'Failed to create checkout session. Please try again or contact support.',
                            });
                          }
                        } catch (error) {
                          console.error('Error creating checkout:', error);
                          setErrorModal({
                            show: true,
                            title: 'Error',
                            message: 'An unexpected error occurred. Please try again later.',
                          });
                        }
                      }}
                      className="w-full py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors"
                    >
                      Choose Premium
                    </button>
                  </div>

                  <div className="border-2 border-purple-200 dark:border-purple-700 rounded-lg p-5 bg-gradient-to-br from-purple-50 to-pink-50 dark:from-purple-900/20 dark:to-pink-900/20">
                    <h3 className="text-xl font-bold text-purple-900 dark:text-purple-100 mb-2">Enterprise</h3>
                    <p className="text-3xl font-bold text-purple-600 dark:text-purple-400 mb-4">$49.00<span className="text-base font-normal text-purple-700 dark:text-purple-300">/month</span></p>
                    <ul className="space-y-2 mb-4">
                      <li className="flex items-start gap-2 text-sm text-purple-900 dark:text-purple-100">
                        <span className="text-purple-600 dark:text-purple-400 mt-0.5">✓</span>
                        <span>Unlimited receipts</span>
                      </li>
                      <li className="flex items-start gap-2 text-sm text-purple-900 dark:text-purple-100">
                        <span className="text-purple-600 dark:text-purple-400 mt-0.5">✓</span>
                        <span>Team collaboration</span>
                      </li>
                      <li className="flex items-start gap-2 text-sm text-purple-900 dark:text-purple-100">
                        <span className="text-purple-600 dark:text-purple-400 mt-0.5">✓</span>
                        <span>Priority support</span>
                      </li>
                      <li className="flex items-start gap-2 text-sm text-purple-900 dark:text-purple-100">
                        <span className="text-purple-600 dark:text-purple-400 mt-0.5">✓</span>
                        <span>Custom integrations</span>
                      </li>
                    </ul>
                    <button
                      onClick={async () => {
                        try {
                          // Get current user
                          console.log('Payment button clicked, devModeUserId:', devModeUserId);

                          const { data: user, error: userError } = await supabase
                            .from('users')
                            .select('telegram_id, first_name, last_name')
                            .eq('id', devModeUserId)
                            .maybeSingle();

                          console.log('User lookup result:', { user, userError });

                          if (userError) {
                            console.error('Error fetching user:', userError);
                            setErrorModal({
                              show: true,
                              title: 'Database Error',
                              message: `Failed to fetch user data: ${userError.message}`,
                            });
                            return;
                          }

                          if (!user?.telegram_id) {
                            setErrorModal({
                              show: true,
                              title: 'User Not Found',
                              message: 'We could not find your user account. Please make sure you are logged in through the Telegram bot first.',
                            });
                            return;
                          }

                          // Create checkout session via edge function
                          const response = await fetch(
                            `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/stripe-webhook`,
                            {
                              method: 'POST',
                              headers: {
                                'Content-Type': 'application/json',
                                'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
                              },
                              body: JSON.stringify({
                                action: 'create_checkout',
                                plan: 'enterprise',
                                user_id: user.telegram_id,
                                email: user.email,
                                success_url: `${window.location.origin}?session_id={CHECKOUT_SESSION_ID}`,
                                cancel_url: window.location.href,
                              }),
                            }
                          );

                          const data = await response.json();

                          if (data.url) {
                            // Open Stripe checkout in new tab to avoid iframe restrictions
                            window.open(data.url, '_blank');
                          } else if (data.error) {
                            let errorMessage = data.error;

                            // Provide more helpful message if Stripe is not configured
                            if (data.error.includes('not configured')) {
                              errorMessage = 'Payment system is currently being set up. Please contact the administrator or try again later.';
                            }

                            setErrorModal({
                              show: true,
                              title: 'Payment Unavailable',
                              message: errorMessage,
                            });
                          } else {
                            setErrorModal({
                              show: true,
                              title: 'Payment Error',
                              message: 'Failed to create checkout session. Please try again or contact support.',
                            });
                          }
                        } catch (error) {
                          console.error('Error creating checkout:', error);
                          setErrorModal({
                            show: true,
                            title: 'Error',
                            message: 'An unexpected error occurred. Please try again later.',
                          });
                        }
                      }}
                      className="w-full py-2 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white rounded-lg font-medium transition-colors"
                    >
                      Choose Enterprise
                    </button>
                  </div>
                </div>
              </div>

              <p className="text-center text-sm text-gray-600 dark:text-gray-400">
                Need help choosing? Contact our sales team for personalized recommendations.
              </p>
            </div>
          </div>
        </div>
      )}

      <Modal
        isOpen={errorModal.show}
        onClose={() => setErrorModal({ show: false, title: '', message: '' })}
        title={errorModal.title}
        type="error"
      >
        <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
          {errorModal.message}
        </p>
      </Modal>
    </div>
  );
}
